import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { usersApi } from '@/api/clients/apiClient'
import { RoleBadge } from '@/components/common/StatusBadge'
import type { UserRole } from '@/api/types'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatRelative(iso?: string | null) {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  return formatDate(iso)
}

const inputCls =
  'px-3 py-1.5 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50'

export default function Team() {
  const { orgId, role: myRole, userId: myUserId } = useAuthStore()
  const queryClient = useQueryClient()

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [resetPwdUserId, setResetPwdUserId] = useState<string | null>(null)
  const [resetPwd, setResetPwd] = useState('')
  const [resetPwdConfirm, setResetPwdConfirm] = useState('')

  const [isAdding, setIsAdding] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newRole, setNewRole] = useState<UserRole>('member')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['users', orgId],
    queryFn: () => usersApi.list(orgId!),
    enabled: !!orgId,
  })

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      usersApi.updateRole(orgId!, userId, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users', orgId] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => usersApi.remove(orgId!, userId),
    onSuccess: () => {
      setConfirmDeleteId(null)
      queryClient.invalidateQueries({ queryKey: ['users', orgId] })
    },
  })

  const createMutation = useMutation({
    mutationFn: () => usersApi.create(orgId!, { email: newEmail.trim(), password: newPassword, role: newRole }),
    onSuccess: () => {
      setIsAdding(false)
      setNewEmail('')
      setNewPassword('')
      setNewRole('member')
      queryClient.invalidateQueries({ queryKey: ['users', orgId] })
    },
  })

  const resetPwdMutation = useMutation({
    mutationFn: (userId: string) =>
      usersApi.changePassword(orgId!, userId, { new_password: resetPwd }),
    onSuccess: () => {
      setResetPwdUserId(null)
      setResetPwd('')
      setResetPwdConfirm('')
    },
  })

  const canManage = myRole === 'owner' || myRole === 'admin'
  const users = data?.data ?? []

  function openResetPwd(userId: string) {
    setResetPwdUserId(userId)
    setResetPwd('')
    setResetPwdConfirm('')
    resetPwdMutation.reset()
    setConfirmDeleteId(null)
  }

  function closeResetPwd() {
    setResetPwdUserId(null)
    setResetPwd('')
    setResetPwdConfirm('')
    resetPwdMutation.reset()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-icons animate-spin text-primary text-4xl">sync</span>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-sm text-red-700 dark:text-red-300">Failed to load team members.</p>
      </div>
    )
  }

  const colCount = canManage ? 5 : 4

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Team</h1>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
            {users.length} member{users.length !== 1 ? 's' : ''}
          </p>
        </div>
        {canManage && !isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
          >
            <span className="material-icons text-base">person_add</span>
            Add member
          </button>
        )}
      </div>

      {/* Add member form */}
      {isAdding && (
        <div className="p-4 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg">
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-3">
            Add new member
          </h3>
          {createMutation.isError && (
            <p className="text-sm text-error mb-2">
              {(createMutation.error as Error)?.message ?? 'Failed to add member.'}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="email"
              placeholder="Email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <input
              type="password"
              placeholder="Initial password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as UserRole)}
              className="px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {(['member', 'admin', 'owner'] as UserRole[]).map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <button
              onClick={() => createMutation.mutate()}
              disabled={!newEmail.trim() || !newPassword || createMutation.isPending}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 text-sm font-medium"
            >
              {createMutation.isPending && (
                <span className="material-icons text-base animate-spin">sync</span>
              )}
              Add
            </button>
            <button
              onClick={() => {
                setIsAdding(false)
                setNewEmail('')
                setNewPassword('')
                setNewRole('member')
                createMutation.reset()
              }}
              className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg text-sm text-text-light dark:text-text-dark hover:bg-background-light dark:hover:bg-background-dark"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface-light dark:bg-surface-dark rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-light dark:bg-background-dark">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Email
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Role
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Last login
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Joined
                </th>
                {canManage && (
                  <th className="px-6 py-3 text-right font-medium text-subtle-light dark:text-subtle-dark">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <>
                  <tr
                    key={user.id}
                    className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark"
                  >
                    <td className="px-6 py-3 text-text-light dark:text-text-dark">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                          <span className="material-icons text-primary text-sm">person</span>
                        </div>
                        <span className="truncate max-w-[220px]">{user.email}</span>
                        {user.id === myUserId && (
                          <span className="text-xs text-subtle-light dark:text-subtle-dark">(you)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      {canManage && user.id !== myUserId ? (
                        <select
                          value={user.role}
                          onChange={(e) =>
                            updateRoleMutation.mutate({ userId: user.id, role: e.target.value })
                          }
                          disabled={updateRoleMutation.isPending}
                          className="text-xs rounded border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark px-2 py-1"
                        >
                          {(['owner', 'admin', 'member'] as UserRole[]).map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <RoleBadge role={user.role} />
                      )}
                    </td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark">
                      {formatRelative(user.last_login_at)}
                    </td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark">
                      {formatDate(user.created_at)}
                    </td>
                    {canManage && (
                      <td className="px-6 py-3 text-right">
                        {user.id !== myUserId && (
                          <>
                            {confirmDeleteId === user.id ? (
                              <div className="flex items-center justify-end gap-2">
                                <span className="text-xs text-subtle-light dark:text-subtle-dark">
                                  Remove user?
                                </span>
                                <button
                                  onClick={() => deleteMutation.mutate(user.id)}
                                  disabled={deleteMutation.isPending}
                                  className="text-xs px-2 py-1 bg-error text-white rounded hover:bg-red-600 disabled:opacity-60"
                                >
                                  {deleteMutation.isPending ? '…' : 'Yes'}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  className="text-xs px-2 py-1 border border-border-light dark:border-border-dark rounded hover:bg-background-light dark:hover:bg-background-dark text-text-light dark:text-text-dark"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => openResetPwd(user.id)}
                                  className="text-subtle-light dark:text-subtle-dark hover:text-primary p-1 rounded"
                                  title="Reset password"
                                >
                                  <span className="material-icons text-lg">lock_reset</span>
                                </button>
                                <button
                                  onClick={() => {
                                    closeResetPwd()
                                    setConfirmDeleteId(user.id)
                                  }}
                                  className="text-subtle-light dark:text-subtle-dark hover:text-error p-1 rounded"
                                  title="Remove user"
                                >
                                  <span className="material-icons text-lg">person_remove</span>
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    )}
                  </tr>

                  {/* Inline reset-password form */}
                  {resetPwdUserId === user.id && (
                    <tr
                      key={`${user.id}-reset`}
                      className="border-t border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark"
                    >
                      <td colSpan={colCount} className="px-6 py-4">
                        <div className="flex flex-wrap items-start gap-3">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-subtle-light dark:text-subtle-dark">
                              New password for <span className="text-text-light dark:text-text-dark">{user.email}</span>
                            </span>
                            {resetPwdMutation.isError && (
                              <p className="text-xs text-error">
                                {(resetPwdMutation.error as Error)?.message ?? 'Failed to reset password.'}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-auto">
                            <input
                              type="password"
                              placeholder="New password"
                              value={resetPwd}
                              onChange={(e) => setResetPwd(e.target.value)}
                              className={inputCls}
                            />
                            <input
                              type="password"
                              placeholder="Confirm password"
                              value={resetPwdConfirm}
                              onChange={(e) => setResetPwdConfirm(e.target.value)}
                              className={inputCls}
                            />
                            <button
                              onClick={() => resetPwdMutation.mutate(user.id)}
                              disabled={
                                !resetPwd ||
                                resetPwd.length < 8 ||
                                resetPwd !== resetPwdConfirm ||
                                resetPwdMutation.isPending
                              }
                              className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 text-sm font-medium"
                            >
                              {resetPwdMutation.isPending && (
                                <span className="material-icons text-base animate-spin">sync</span>
                              )}
                              Reset
                            </button>
                            <button
                              onClick={closeResetPwd}
                              className="px-3 py-1.5 border border-border-light dark:border-border-dark rounded-lg text-sm text-text-light dark:text-text-dark hover:bg-surface-light dark:hover:bg-surface-dark"
                            >
                              Cancel
                            </button>
                          </div>
                          {resetPwd && resetPwd.length < 8 && (
                            <p className="w-full text-xs text-error">Min 8 characters.</p>
                          )}
                          {resetPwdConfirm && resetPwd !== resetPwdConfirm && (
                            <p className="w-full text-xs text-error">Passwords do not match.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}

              {users.length === 0 && (
                <tr>
                  <td
                    colSpan={colCount}
                    className="px-6 py-8 text-center text-subtle-light dark:text-subtle-dark"
                  >
                    No team members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
